// src/features/health/hooks/useHealthReports.ts
// React Query hooks for health report generation and management

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/core/lib/api';
import type {
  HealthReport,
  ReportType,
  ExportFormat,
  GenerateReportRequest,
  GenerateReportResponse,
  ExportReportRequest,
  ExportReportResponse,
  ReportHistoryParams,
  ReportHistoryResponse,
  ReportPreviewData,
  ReportGenerationFormData,
  ExportOptionsFormData,
  ReportTimeframe,
} from '../types/reportTypes';
import { format, subDays, subMonths } from 'date-fns';

// ============================================================================
// Query Keys
// ============================================================================

export const reportKeys = {
  all: ['health-reports'] as const,
  history: () => [...reportKeys.all, 'history'] as const,
  historyList: (params: ReportHistoryParams) => [...reportKeys.history(), params] as const,
  details: () => [...reportKeys.all, 'detail'] as const,
  detail: (id: string) => [...reportKeys.details(), id] as const,
  preview: () => [...reportKeys.all, 'preview'] as const,
  previewData: (childId: string, params: Partial<GenerateReportRequest>) =>
    [...reportKeys.preview(), childId, params] as const,
};

// ============================================================================
// API Functions
// ============================================================================

const generateReport = async (request: GenerateReportRequest): Promise<GenerateReportResponse> => {
  const { data } = await api.post('/health-reports/generate', request);
  return data;
};

const fetchReport = async (reportId: string): Promise<HealthReport> => {
  const { data } = await api.get(`/health-reports/${reportId}`);
  return data;
};

const fetchReportHistory = async (params: ReportHistoryParams): Promise<ReportHistoryResponse> => {
  const { data } = await api.get(`/children/${params.childId}/health-reports`, {
    params: {
      page: params.page || 1,
      limit: params.limit || 10,
    },
  });
  return data;
};

const exportReport = async (request: ExportReportRequest): Promise<ExportReportResponse> => {
  const { data } = await api.post(`/health-reports/${request.reportId}/export`, {
    format: request.format,
    options: request.options,
  });
  return data;
};

const deleteReport = async (reportId: string): Promise<void> => {
  await api.delete(`/health-reports/${reportId}`);
};

const previewReport = async (request: GenerateReportRequest): Promise<ReportPreviewData> => {
  const { data } = await api.post('/health-reports/preview', request);
  return data;
};

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Fetch report history for a child
 */
export function useReportHistory(params: ReportHistoryParams) {
  return useQuery({
    queryKey: reportKeys.historyList(params),
    queryFn: () => fetchReportHistory(params),
    enabled: !!params.childId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Fetch a specific report by ID
 */
export function useReport(reportId: string | undefined) {
  return useQuery({
    queryKey: reportKeys.detail(reportId!),
    queryFn: () => fetchReport(reportId!),
    enabled: !!reportId,
    staleTime: 10 * 60 * 1000, // 10 minutes - reports don't change
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Generate a new health report
 */
export function useGenerateReport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: generateReport,
    onSuccess: (data, variables) => {
      // Invalidate report history
      queryClient.invalidateQueries({
        queryKey: reportKeys.history(),
        predicate: (query) => {
          const params = query.queryKey[2] as ReportHistoryParams | undefined;
          return params?.childId === variables.childId;
        },
      });
      // Cache the new report
      queryClient.setQueryData(reportKeys.detail(data.report.id), data.report);
    },
  });
}

/**
 * Export a report to a specific format
 */
export function useExportReport() {
  return useMutation({
    mutationFn: exportReport,
    onSuccess: (data) => {
      // Trigger download
      if (data.downloadUrl) {
        window.open(data.downloadUrl, '_blank');
      }
    },
  });
}

/**
 * Delete a report
 */
export function useDeleteReport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteReport,
    onSuccess: (_, reportId) => {
      // Remove from cache
      queryClient.removeQueries({
        queryKey: reportKeys.detail(reportId),
      });
      // Invalidate history
      queryClient.invalidateQueries({
        queryKey: reportKeys.history(),
      });
    },
  });
}

/**
 * Preview a report before generating
 */
export function usePreviewReport() {
  return useMutation({
    mutationFn: previewReport,
  });
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Convert timeframe to date range
 */
export function timeframeToDateRange(
  timeframe: ReportTimeframe,
  customDates?: { startDate: string; endDate: string }
): { startDate: string; endDate: string } {
  const today = new Date();
  const formatDate = (date: Date) => format(date, 'yyyy-MM-dd');

  switch (timeframe) {
    case 'last_7_days':
      return {
        startDate: formatDate(subDays(today, 7)),
        endDate: formatDate(today),
      };
    case 'last_30_days':
      return {
        startDate: formatDate(subDays(today, 30)),
        endDate: formatDate(today),
      };
    case 'last_90_days':
      return {
        startDate: formatDate(subDays(today, 90)),
        endDate: formatDate(today),
      };
    case 'last_6_months':
      return {
        startDate: formatDate(subMonths(today, 6)),
        endDate: formatDate(today),
      };
    case 'last_year':
      return {
        startDate: formatDate(subMonths(today, 12)),
        endDate: formatDate(today),
      };
    case 'custom':
      if (!customDates) {
        throw new Error('Custom timeframe requires customDates');
      }
      return customDates;
    default:
      return {
        startDate: formatDate(subDays(today, 30)),
        endDate: formatDate(today),
      };
  }
}

/**
 * Convert form data to API request
 */
export function formDataToRequest(
  formData: ReportGenerationFormData
): GenerateReportRequest {
  const dateRange = timeframeToDateRange(
    formData.timeframe,
    formData.customStartDate && formData.customEndDate
      ? { startDate: formData.customStartDate, endDate: formData.customEndDate }
      : undefined
  );

  return {
    childId: formData.childId,
    reportType: formData.reportType,
    title: formData.title,
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
    includeSections: formData.includeSections,
    logTypes: formData.logTypes,
    includeNotes: formData.includeNotes,
    includeAttachments: formData.includeAttachments,
  };
}

/**
 * Generate default report title based on type and date range
 */
export function generateDefaultTitle(
  reportType: ReportType,
  childName: string,
  startDate: string,
  endDate: string
): string {
  const typeLabels: Record<ReportType, string> = {
    comprehensive: 'Comprehensive Health Report',
    health_logs: 'Health Logs Report',
    medications: 'Medications Report',
    symptoms: 'Symptoms Report',
    seizures: 'Seizure Report',
    appointments: 'Appointments Report',
    custom: 'Custom Health Report',
  };

  const start = format(new Date(startDate), 'MMM d');
  const end = format(new Date(endDate), 'MMM d, yyyy');

  return `${childName} - ${typeLabels[reportType]} (${start} - ${end})`;
}

// ============================================================================
// Combined Hook for Report Generation Flow
// ============================================================================

export interface UseReportGenerationReturn {
  isGenerating: boolean;
  isExporting: boolean;
  generateError: Error | null;
  exportError: Error | null;
  generatedReport: HealthReport | null;
  generate: (formData: ReportGenerationFormData) => Promise<HealthReport>;
  export: (
    reportId: string,
    format: ExportFormat,
    options?: Partial<ExportOptionsFormData>
  ) => Promise<string>;
  reset: () => void;
}

export function useReportGeneration(): UseReportGenerationReturn {
  const generateMutation = useGenerateReport();
  const exportMutation = useExportReport();

  const generate = async (formData: ReportGenerationFormData): Promise<HealthReport> => {
    const request = formDataToRequest(formData);
    const response = await generateMutation.mutateAsync(request);
    return response.report;
  };

  const exportFn = async (
    reportId: string,
    format: ExportFormat,
    options: Partial<ExportOptionsFormData> = {}
  ): Promise<string> => {
    const response = await exportMutation.mutateAsync({
      reportId,
      format,
      options: {
        format,
        includeHeader: options.includeHeader ?? true,
        includeTimestamp: options.includeTimestamp ?? true,
        paperSize: options.paperSize ?? 'letter',
        orientation: options.orientation ?? 'portrait',
        filename: options.filename,
      },
    });
    return response.downloadUrl;
  };

  const reset = () => {
    generateMutation.reset();
    exportMutation.reset();
  };

  return {
    isGenerating: generateMutation.isPending,
    isExporting: exportMutation.isPending,
    generateError: generateMutation.error,
    exportError: exportMutation.error,
    generatedReport: generateMutation.data?.report ?? null,
    generate,
    export: exportFn,
    reset,
  };
}

// ============================================================================
// Quick Report Generation Hooks
// ============================================================================

/**
 * Generate a quick comprehensive report for the last 30 days
 */
export function useQuickComprehensiveReport(childId: string, childName: string) {
  const { generate, ...rest } = useReportGeneration();

  const generateQuick = async () => {
    const today = new Date();
    const thirtyDaysAgo = subDays(today, 30);

    return generate({
      childId,
      reportType: 'comprehensive' as ReportType,
      title: generateDefaultTitle(
        'comprehensive' as ReportType,
        childName,
        format(thirtyDaysAgo, 'yyyy-MM-dd'),
        format(today, 'yyyy-MM-dd')
      ),
      timeframe: 'last_30_days' as ReportTimeframe,
      includeSections: {
        healthLogs: true,
        medications: true,
        adherence: true,
        trends: true,
        charts: true,
      },
      includeNotes: false,
      includeAttachments: false,
    });
  };

  return {
    ...rest,
    generateQuick,
  };
}

